/**
 * EventPrizeDisplayToggles — compact inline prize-display switches for a single
 * event row in the My Rodeos list. Rendered right-aligned in the (always
 * visible) event header so a delegate can flip them without expanding the row
 * or opening the Edit Event dialog.
 *
 * The three switches map 1:1 to the same fields the Edit Event dialog writes;
 * the verbose labels there are shortened to one word here, with the full dialog
 * wording kept in each switch's tooltip:
 *   1. Contestants → display_prize_to_contestants
 *   2. Results     → show_prize_amounts_in_results
 *   3. Live        → show_prize_amounts_before_complete
 *
 * Dependency invariant (same as the dialog): "Live" is only meaningful when
 * "Results" is ON — its switch is disabled and reads OFF whenever "Results" is
 * off, and turning "Results" off clears it. The parent owns the values and
 * persistence (this component is fully controlled).
 */

import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export interface EventPrizeDisplayTogglesValues {
  display_prize_to_contestants: boolean;
  show_prize_amounts_in_results: boolean;
  show_prize_amounts_before_complete: boolean;
}

/**
 * One-word labels for the three prize-display switches, shared with the
 * rodeo-level master toggles so both surfaces stay in lockstep.
 */
export const PRIZE_TOGGLE_LABELS = {
  display_prize_to_contestants: 'Contestants',
  show_prize_amounts_in_results: 'Results',
  show_prize_amounts_before_complete: 'Live',
} as const;

export interface EventPrizeDisplayTogglesProps {
  values: EventPrizeDisplayTogglesValues;
  /**
   * Receives the FULL next value set with the dependency invariant already
   * enforced (turning results off clears before-complete). The parent persists.
   */
  onChange: (next: EventPrizeDisplayTogglesValues) => void;
  /** Disable all switches while a save is in flight. */
  disabled?: boolean;
  /** Unique prefix so each event's switch ids/labels stay distinct in the list. */
  idPrefix: string;
}

interface CompactToggleProps {
  id: string;
  label: string;
  title: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (v: boolean) => void;
}

function CompactToggle({
  id,
  label,
  title,
  checked,
  disabled,
  onCheckedChange,
}: CompactToggleProps) {
  return (
    <div
      className={`flex flex-col items-center gap-1 ${disabled ? 'opacity-50' : ''}`}
      title={title}
    >
      <Label
        htmlFor={id}
        className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground leading-none cursor-pointer"
      >
        {label}
      </Label>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="scale-[0.7]"
      />
    </div>
  );
}

export function EventPrizeDisplayToggles({
  values,
  onChange,
  disabled,
  idPrefix,
}: EventPrizeDisplayTogglesProps) {
  const inResults = values.show_prize_amounts_in_results;
  const beforeComplete = inResults && values.show_prize_amounts_before_complete;

  return (
    <div className="flex items-center gap-2.5">
      <CompactToggle
        id={`${idPrefix}-display_prize_to_contestants`}
        label={PRIZE_TOGGLE_LABELS.display_prize_to_contestants}
        title="Show prize to contestants — when on, contestants see this event's prize and it rolls into the rodeo prize totals."
        checked={values.display_prize_to_contestants}
        disabled={disabled}
        onCheckedChange={(v) =>
          onChange({ ...values, display_prize_to_contestants: v })
        }
      />
      <CompactToggle
        id={`${idPrefix}-show_prize_amounts_in_results`}
        label={PRIZE_TOGGLE_LABELS.show_prize_amounts_in_results}
        title="Show prize amounts in results — prize amounts appear next to placed contestants once the event is fully scored. Turn off to hide prize allocations from the results entirely."
        checked={inResults}
        disabled={disabled}
        onCheckedChange={(v) =>
          onChange({
            ...values,
            show_prize_amounts_in_results: v,
            // Turning results off clears the dependent before-complete flag.
            show_prize_amounts_before_complete: v
              ? values.show_prize_amounts_before_complete
              : false,
          })
        }
      />
      <CompactToggle
        id={`${idPrefix}-show_prize_amounts_before_complete`}
        label={PRIZE_TOGGLE_LABELS.show_prize_amounts_before_complete}
        title="Show prize amounts before all contestants compete — reveal the running amounts live as the event runs, before everyone has been scored. Requires “Results” to be on."
        checked={beforeComplete}
        disabled={disabled || !inResults}
        onCheckedChange={(v) =>
          onChange({ ...values, show_prize_amounts_before_complete: v })
        }
      />
    </div>
  );
}

export default EventPrizeDisplayToggles;
